import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Label } from '@/components/ui/label';
import Title from '@/components/ui/Title';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  deleteWebinarSlide,
  finalizeWebinarSlideUpload,
  generateWebinarSlideUploadUrl,
  listWebinarSlides,
} from '@/features/webinar/webinarThunks';
import { useTranslation } from 'react-i18next';
import PdfNotesForm from './PdfNotesForm';
import * as pdfjsLib from 'pdfjs-dist';
import EmptyInputFile from '../EmptyInputFile';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const getPdfPageCount = async (file: File): Promise<number | null> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDocument = await loadingTask.promise;
    return pdfDocument.numPages;
  } catch (error) {
    console.error('Failed to extract PDF page count', error);
    return null;
  }
};

const bytesToMB = (bytes: number): string => {
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const PDF_ACCORDION_VALUE = 'item-1';

export default function PdfsForm({
  webinarId,
  workspaceId,
  isEdit,
  openAccordionTrigger,
  onAccordionOpened,
}: {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  openAccordionTrigger?: boolean;
  onAccordionOpened?: () => void;
}) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();

  const formSchema = z.object({
    pdf: z
      .instanceof(File)
      .refine((file) => file.size <= 100 * 1024 * 1024, {
        message: t('pdfsForm.validation.fileSizeTooLarge'),
      })
      .refine(
        (file) => {
          const allowedTypes = ['application/pdf'];
          return allowedTypes.includes(file.type);
        },
        {
          message: t('pdfsForm.validation.unsupportedPdfFormat'),
        },
      ),
  });

  const {
    webinarSlides,
    webinarSlidesLoading,
    webinarSlideUploadInitLoading,
    webinarSlideFinalizeLoading,
    webinarSlidesError,
  } = useAppSelector((state: RootState) => state.webinar);

  const pdfSlides = webinarSlides?.filter((slide) => slide.mimeType === 'application/pdf');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pdf: undefined,
    },
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPdfId, setSelectedPdfId] = useState<number | null>(null);
  const [accordionValue, setAccordionValue] = useState<string>('item-1');

  useEffect(() => {
    void dispatch(listWebinarSlides({ workspaceId, webinarId }));
  }, [dispatch, webinarId, workspaceId]);

  useEffect(() => {
    if (openAccordionTrigger) {
      setAccordionValue(PDF_ACCORDION_VALUE);
      onAccordionOpened?.();
    }
  }, [openAccordionTrigger, onAccordionOpened]);

  const uploadFileToUrl = async (url: string, file: File) => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(t('pdfsForm.errors.uploadFailed'));
    }
  };

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    const { pdf } = data;
    if (!pdf) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const uploadInit = await dispatch(
        generateWebinarSlideUploadUrl({
          workspaceId,
          webinarId,
          input: {
            fileName: pdf.name,
            mimeType: pdf.type,
            sizeBytes: pdf.size,
          },
        }),
      ).unwrap();

      await uploadFileToUrl(uploadInit.uploadUrl, pdf);
      const pageCount = await getPdfPageCount(pdf);

      await dispatch(
        finalizeWebinarSlideUpload({
          workspaceId,
          webinarId,
          input: {
            slideKey: uploadInit.slideKey,
            fileName: pdf.name,
            mimeType: pdf.type,
            sizeBytes: pdf.size,
            pageCount: pageCount ?? null,
            checksumSha256: null,
            position: null,
          },
        }),
      ).unwrap();

      setSelectedFile(null);
      form.reset();
    } catch (error) {
      const err = error as { message?: string };
      setUploadError(err?.message ?? t('pdfsForm.errors.unexpectedUploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      form.setValue('pdf', file);
      void handleSubmit({ pdf: file });
    }
  };

  const handleDeletePdf = (slideId: number) => {
    setUploadError(null);
    dispatch(deleteWebinarSlide({ workspaceId, webinarId, slideId }));
  };

  if (webinarSlidesLoading && !pdfSlides?.length) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="item-1">
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger className=" rounded-[20px]">
              <Title level={2}>{t('pdfsForm.title')}</Title>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 rounded-[20px] bg-white pt-4 p-0">
              <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="pt-4">
          <Skeleton className="h-10 w-40" />
        </div>
      </div>
    );
  }

  const isButtonLoading =
    isUploading || webinarSlideUploadInitLoading || webinarSlideFinalizeLoading;

  return (
    <div>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="item-1" className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}>
          <AccordionTrigger className=" rounded-[20px]" fillForm={webinarSlides.length > 0}>
            <div>
              <Title level={2}>{t('pdfsForm.title')}</Title>
              <p className="text-sm font-normal text-tertiary">{t('pdfsForm.description')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4 bg-subtitle' : ''}`}
          >
            <Accordion type="single" collapsible className="space-y-[10px]" defaultValue="item-0">
              {!pdfSlides || pdfSlides.length === 0 ? (
                <div onClick={() => document.getElementById('pdf-upload')?.click()}>
                  <EmptyInputFile
                    contentType={t('pdfsForm.emptyContentType')}
                    type="pdf"
                    isLoading={isButtonLoading}
                  />
                </div>
              ) : (
                pdfSlides.map((pdf) => (
                  <div key={pdf.id}>
                    <Label
                      className={`p-4 bg-white border border-medium rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedPdfId === pdf.id
                          ? 'rounded-b-none border-b-0'
                          : 'rounded-b-none border-b-0'
                      }`}
                      // className="p-4 bg-white border border-medium rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedPdfId(selectedPdfId === pdf.id ? null : pdf.id)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="size-10 bg-medium rounded-lg flex items-center justify-center">
                          <img src="/assets/attach.svg" alt={t('pdfsForm.attachIcon')} />
                        </div>
                        <div className="flex flex-col gap-2">
                          <span>{pdf.fileName}</span>
                          <p className="text-sm text-gray-tertiary">{bytesToMB(pdf.sizeBytes)}</p>
                        </div>
                      </div>
                      <Trash2
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePdf(pdf.id);
                        }}
                      />
                    </Label>
                    <PdfNotesForm asset={pdf} workspaceId={workspaceId} webinarId={webinarId} />
                  </div>
                ))
              )}
            </Accordion>

            <input
              type="file"
              id="pdf-upload"
              className="sr-only"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              disabled={isButtonLoading}
            />
            {pdfSlides && pdfSlides.length > 0 && (
              <div className="pt-4">
                <button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => document.getElementById('pdf-upload')?.click()}
                  disabled={isButtonLoading}
                >
                  {isButtonLoading ? (
                    <Loader2 className="size-6 animate-spin" />
                  ) : (
                    <Plus className="size-6" />
                  )}
                  <Label className="cursor-pointer">
                    {isButtonLoading ? t('pdfsForm.uploading') : t('pdfsForm.addPdf')}
                  </Label>
                </button>
                {(uploadError || webinarSlidesError) && (
                  <p className="text-sm text-error mt-2">{uploadError ?? webinarSlidesError}</p>
                )}
                {selectedFile && !isButtonLoading && (
                  <p className="text-sm text-tertiary mt-2">
                    {t('pdfsForm.selectedFile')} {selectedFile.name}
                  </p>
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
